import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Divider, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../components/InHouseTooltip/InHouseTooltip";
import { SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemSwitch } from "../../components/UIToolkit/Clickable/SystemSwitch";
import { CollapsibleSection } from "../../components/UIToolkit/Panels/CollapsibleSection";
import type { Form530Settings, Shift530Filter } from "../../downloads/form530/pages";
import type { PrintoutSettings } from "./Header/usePrintout";
import { FitToPageSettings } from "./PrintoutSettingsControls";

type Form530SettingsPanelProps = {
	form530Settings: Form530Settings;
	updateForm530Setting: <K extends keyof Form530Settings>(key: K, value: Form530Settings[K]) => void;
	printoutSettings: PrintoutSettings;
	updateSetting: <K extends keyof PrintoutSettings>(key: K, value: PrintoutSettings[K]) => void;
};

/**
 * Settings for the CDPH 530 tab, laid out like the day view's own panel so the download dialog reads
 * as one screen. The form's layout is fixed by the state, so the only choices are which shift to
 * produce, which columns arrive pre-filled (APP-647), and the shared Excel scale-to-fit page setup.
 *
 * The two pre-fill toggles are per-session, not saved to user settings: unlike the layout settings
 * next to them they change what the state form ASSERTS about a patient day, so each download is an
 * explicit choice rather than something a teammate's saved default silently turns on.
 */
export const Form530SettingsPanel: FC<Form530SettingsPanelProps> = ({ form530Settings, updateForm530Setting, printoutSettings, updateSetting }) => {
	const { t } = useTranslation();
	const [layoutOpen, setLayoutOpen] = useState<boolean>(true);

	const shiftOptions: { label: string; value: Shift530Filter }[] = [
		{ label: t("download.530.shift.all"), value: "all" },
		{ label: t("download.530.shift.morning"), value: "1" },
		{ label: t("download.530.shift.evening"), value: "2" },
		{ label: t("download.530.shift.night"), value: "3" },
	];

	return (
		<Box
			data-testid="day-schedule-530-settings"
			sx={{
				height: "100%",
				border: "1px solid #e0e0e0",
				borderRadius: 1,
				p: 2,
				overflowY: "auto",
			}}
		>
			<Typography sx={{ color: "primary.dark", fontWeight: 600, fontSize: "18px", mb: 2 }}>{t("printout.settings")}</Typography>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
				<Typography variant="body2" sx={{ color: "primary.dark", fontWeight: 600 }}>
					{t("download.530.select.shift")}
				</Typography>
				<SystemFlatSwitch
					data-testid="day-schedule-530-shift-switch"
					options={shiftOptions}
					activeIndexOrValue={form530Settings.shiftFilter}
					onSwitch={(option) => updateForm530Setting("shiftFilter", option.value)}
				/>
			</Box>

			<Divider sx={{ my: 1 }} />

			<CollapsibleSection title={t("printout.530.prefill")} open={layoutOpen} onToggle={() => setLayoutOpen(!layoutOpen)}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
					<Typography sx={{ color: "#8B8B8B", fontSize: "11px" }}>{t("printout.530.prefill.hint")}</Typography>

					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<SystemSwitch
							dataTestId="printout-530-show-shift-times-switch"
							checked={form530Settings.showShiftTimes}
							onChange={() => updateForm530Setting("showShiftTimes", !form530Settings.showShiftTimes)}
							label={t("printout.530.show.shift.times")}
						/>
						<InHouseTooltip title={t("printout.530.show.shift.times.tooltip")}>
							<InfoOutlined sx={{ fontSize: "18px" }} />
						</InHouseTooltip>
					</Box>

					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<SystemSwitch
							dataTestId="printout-530-show-locations-switch"
							checked={form530Settings.showLocations}
							onChange={() => updateForm530Setting("showLocations", !form530Settings.showLocations)}
							label={t("printout.530.show.locations")}
						/>
						<InHouseTooltip title={t("printout.530.show.locations.tooltip")}>
							<InfoOutlined sx={{ fontSize: "18px" }} />
						</InHouseTooltip>
					</Box>
				</Box>
			</CollapsibleSection>

			<Divider sx={{ my: 1 }} />

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
				<Typography variant="body2" sx={{ color: "primary.dark", fontWeight: 600 }}>
					{t("printout.530.excel.page.setup")}
				</Typography>
				<FitToPageSettings
					fitToPage={printoutSettings.fitToPage}
					fitToWidth={printoutSettings.fitToWidth}
					fitToHeight={printoutSettings.fitToHeight}
					onFitToPageChange={(value) => updateSetting("fitToPage", value)}
					onFitToWidthChange={(value) => updateSetting("fitToWidth", value)}
					onFitToHeightChange={(value) => updateSetting("fitToHeight", value)}
					showPageCounts={false}
				/>
			</Box>
		</Box>
	);
};
